"use client";

import type { ActiveOrganization, Session } from "@repo/auth";
import { authClient } from "@repo/auth/client";
import { isOrganizationAdmin } from "@repo/auth/lib/helper";
import { config } from "@repo/config";
import { useSession } from "@saas/auth/client";
import { authQueryKeys } from "@saas/auth/lib/api";
import {
	fullOrganizationQueryOptions,
	organizationsQueryKeys,
	useActiveOrganizationQuery,
} from "@saas/organizations/lib/api";
import { useRouter } from "@shared/hooks/router";
import { orpc } from "@shared/lib/orpc";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMatch, useParams } from "@tanstack/react-router";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { ActiveOrganizationContext } from "../lib/active-organization-context";

export function ActiveOrganizationProvider({
	children,
}: {
	children: ReactNode;
}) {
	const router = useRouter();
	const queryClient = useQueryClient();
	const { session, user } = useSession();
	const params = useParams({ strict: false });

	const activeOrganizationSlug =
		(params as { organizationSlug?: string }).organizationSlug ?? "";

	// Primary query: fetch by slug from URL params
	const { data: activeOrganizationBySlug } = useActiveOrganizationQuery(
		activeOrganizationSlug,
		{
			enabled: !!activeOrganizationSlug,
		},
	);

	// Fallback query: fetch by session.activeOrganizationId when no slug in URL
	// This preserves org context on pages like Account settings that don't have $organizationSlug
	const { data: activeOrganizationById } = useQuery({
		...fullOrganizationQueryOptions(session?.activeOrganizationId ?? ""),
		enabled: !activeOrganizationSlug && !!session?.activeOrganizationId,
	});

	// The org route's loader already holds the organization (server-fetched on
	// every navigation). Use it until the client query settles, otherwise the
	// shell renders with no organization after a client-side navigation.
	const organizationRoute = useMatch({
		from: "/_saas/app/_org/$organizationSlug",
		shouldThrow: false,
	});

	// Use slug-based data when available, otherwise fall back to ID-based data
	const activeOrganizationData = activeOrganizationSlug
		? (activeOrganizationBySlug ??
			organizationRoute?.loaderData?.organization)
		: activeOrganizationById;

	// Cast to ActiveOrganization type - the query returns a compatible shape
	const activeOrganization = activeOrganizationData as
		| ActiveOrganization
		| null
		| undefined;

	const activeOrganizationId = session?.activeOrganizationId;

	const refetchActiveOrganization = useCallback(async () => {
		if (activeOrganizationSlug) {
			await queryClient.refetchQueries({
				queryKey: organizationsQueryKeys.active(activeOrganizationSlug),
			});
		} else if (activeOrganizationId) {
			await queryClient.refetchQueries({
				queryKey: organizationsQueryKeys.detail(activeOrganizationId),
			});
		}
	}, [queryClient, activeOrganizationSlug, activeOrganizationId]);

	const setActiveOrganization = useCallback(
		async (organizationSlug: string | null) => {
			const { default: nProgress } = await import("nprogress");
			nProgress.start();

			// If clearing organization, pass null directly
			if (!organizationSlug) {
				const { error } = await authClient.organization.setActive({
					organizationId: null,
				});
				nProgress.done();
				if (error) {
					throw new Error(
						error.message || "Failed to clear active organization",
					);
				}
				return;
			}

			// Look up the organization ID from the slug first
			const { data: orgData, error: orgError } =
				await authClient.organization.getFullOrganization({
					query: { organizationSlug },
				});

			if (orgError || !orgData) {
				nProgress.done();
				throw new Error(orgError?.message || "Organization not found");
			}

			// Now set active using the actual organization ID
			const { data, error } = await authClient.organization.setActive({
				organizationId: orgData.id,
			});

			if (error) {
				nProgress.done();
				throw new Error(
					error.message || "Failed to set active organization",
				);
			}

			if (!data) {
				nProgress.done();
				return;
			}

			await refetchActiveOrganization();

			if (config.organizations.enableBilling) {
				await queryClient.prefetchQuery(
					orpc.payments.listPurchases.queryOptions({
						input: {
							organizationId: orgData.id,
						},
					}),
				);
			}

			await queryClient.setQueryData(
				authQueryKeys.session(),
				(sessionData: Session | undefined) => {
					if (!sessionData) {
						return sessionData;
					}
					return {
						...sessionData,
						session: {
							...sessionData.session,
							activeOrganizationId: orgData.id,
						},
					};
				},
			);

			router.push(`/app/${orgData.slug}`);
		},
		[queryClient, refetchActiveOrganization, router],
	);

	// Latches true the first time the organization query has answered. Set
	// while rendering (not in an effect) so consumers never see one stale frame.
	const [loaded, setLoaded] = useState(activeOrganization !== undefined);
	if (!loaded && activeOrganization !== undefined) {
		setLoaded(true);
	}

	const activeOrganizationUserRole = activeOrganization?.members.find(
		(member) => member.userId === session?.userId,
	)?.role;

	const value = useMemo(
		() => ({
			loaded,
			activeOrganization: activeOrganization ?? null,
			activeOrganizationUserRole: activeOrganizationUserRole ?? null,
			isOrganizationAdmin:
				!!activeOrganization &&
				!!user &&
				isOrganizationAdmin(activeOrganization, user),
			setActiveOrganization,
			refetchActiveOrganization,
		}),
		[
			loaded,
			activeOrganization,
			activeOrganizationUserRole,
			user,
			setActiveOrganization,
			refetchActiveOrganization,
		],
	);

	return (
		<ActiveOrganizationContext.Provider value={value}>
			{children}
		</ActiveOrganizationContext.Provider>
	);
}
